import React, { useState } from "react";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import Chip from "@mui/material/Chip";

import { useTranslation } from "react-i18next";
import VideoPlayer from "./VideoPlayer";
import ChessButton from "./ChessButton";
import HowToVoteIcon from "@mui/icons-material/HowToVote";

const GameLevelSelectionItem = ({
  onLevelSelected,
  level_type_id,
  name,
  description,
  imagePath,
  videoPath,
  autoPlay,
  win,
  lose,
  draw,
  win_coins,
  lose_coins,
  draw_coins,
  isSelected,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  const isMobile = window.innerWidth <= 600;
  const mobileFontSize = "12px";
  const regularFontSize = "18px";
  const [accordionExpanded, setAccordionExpanded] = useState(true);

  function handleSelectLevelClick() {
    onLevelSelected();
  }

  const minWidth = "100px";
  const minHeight = "200px";
  const cardColor = "black";

  // Trophy images for different outcomes
  const getTrophyImage = (type) => {
    const trophies = {
      win: "🏆", // Gold trophy emoji
      lose: "💔", // Broken heart emoji
      draw: "🤝", // Handshake emoji
    };
    return trophies[type] || "🏅";
  };

  // Enhanced outcome box component
  const OutcomeBox = ({ type, coins, borderColor, textColor, dividerColor }) => (
    <Box
      sx={{
        textAlign: "center",
        width: isMobile ? "90%" : "30%",
        minWidth: minWidth,
        minHeight: isMobile ? (type === "win" ? "250px" : minHeight) : (type === "win" ? "180px" : "160px"), // Reduced desktop height
        border: `${type === "win" ? "8px" : "5px"} solid ${borderColor}`,
        boxShadow: type === "win" ? "19px 12px 15px black" : "9px 12px 15px black",
        borderRadius: "8px",
        padding: 0,
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(145deg, ${borderColor}10, ${borderColor}05)`,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px) scale(1.02)",
          boxShadow: type === "win" 
            ? "22px 15px 20px rgba(0,0,0,0.4)" 
            : "12px 15px 20px rgba(0,0,0,0.3)",
        }
      }}
    >
      {/* Header with outcome type */}
      <Typography
        variant={isMobile ? "h7" : "h5"}
        sx={{
          fontWeight: type === "win" ? "bold" : "normal",
          color: type === "win" ? "#4a707d" : textColor,
          padding: "8px",
          fontSize: isMobile ? "0.9rem" : "1.1rem",
        }}
      >
        {t(`Game_level_description_${type.charAt(0).toUpperCase() + type.slice(1)}`)}
      </Typography>
      
      <Divider sx={{ bgcolor: dividerColor, marginTop: "1px" }} />
      
      {/* Trophy/Icon section */}
      <Box
        sx={{
          padding: "10px 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? "2rem" : "2.5rem",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
          }}
        >
          {getTrophyImage(type)}
        </Typography>
      </Box>
      
      <Divider sx={{ bgcolor: dividerColor, marginTop: "1px" }} />
      
      {/* Coin and amount section */}
      <Typography
        variant={isMobile ? "h7" : "h4"}
        sx={{
          color: textColor,
          padding: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          fontWeight: type === "win" ? "bold" : "normal",
        }}
      >
        <Box
          component="img"
          src="https://res.cloudinary.com/dnlgtek2a/image/upload/v1743405234/Pictures/coin.png"
          alt="Coin"
          sx={{
            width: { xs: type === "win" ? "1.8rem" : "1.3rem", sm: type === "win" ? "2rem" : "1.5rem" },
            height: { xs: type === "win" ? "1.8rem" : "1.3rem", sm: type === "win" ? "2rem" : "1.5rem" },
            marginBottom: "8px",
            filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.3))",
            animation: "coinSpin 3s linear infinite",
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "scale(1.2)",
              animationDuration: "0.5s",
            },
            "@keyframes coinSpin": {
              "0%": {
                transform: "rotateY(0deg)",
              },
              "50%": {
                transform: "rotateY(180deg) scale(0.9)",
              },
              "100%": {
                transform: "rotateY(360deg)",
              }
            }
          }}
        />
        <Typography
          variant={type === "win" ? "h3" : "h4"}
          sx={{
            fontWeight: 800,
            color: textColor,
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            fontSize: isMobile 
              ? (type === "win" ? "1.4rem" : "1.1rem")
              : (type === "win" ? "2rem" : "1.5rem"),
          }}
        >
          {coins}
        </Typography>
      </Typography>
    </Box>
  );

  return (
    <Card
      onClick={(e) => {
        if (e.target !== e.currentTarget) {
          return;
        }
        onSelect();
      }}
      sx={{
        minWidth: isMobile ? 350 : 700,
        maxWidth: isMobile ? 350 : 700,
        minHeight: isMobile ? 660 : 850,
        maxHeight: isMobile ? 660 : 850,
        borderRadius: "15px",
        position: "relative",
        cursor: "pointer",
        overflow: "hidden",
        border: isSelected ? "3px solid #4a707d" : "1px solid black",
        boxShadow: isSelected 
          ? "0 0 0 4px rgba(74, 112, 125, 0.3), 0 5px 5px black"
          : "0 5px 5px black",
        backgroundColor: cardColor,
        textAlign: "center",
        alignItems: "center",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: isSelected
            ? "0 0 0 4px rgba(74, 112, 125, 0.4), 0 8px 15px black"
            : "0 8px 15px black",
        }
      }}
    >
      <CardMedia
        component="div"
        sx={{
          height: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 0,
        }}
      >
        <VideoPlayer
          isSelected={isSelected}
          videoSrc={videoPath}
          autoPlay={false}
          loop={false}
          muted
          style={{
            width: "100%",
            height: isMobile ? "80%" : "100%",
            borderRadius: "1px",
          }}
        />
      </CardMedia>

      {/* Accordion for Content Section */}
      {level_type_id !== "0" && (
        <Accordion
          expanded={accordionExpanded}
          onChange={(e) => {
            e.stopPropagation();
            setAccordionExpanded(!accordionExpanded);
          }}
          sx={{
            borderRadius: "15px",
            boxShadow: "none",
            minHeight: isMobile ? 580 : 500, // Reduced desktop height
            maxHeight: isMobile ? 780 : 500, // Reduced desktop height
          }}
        >
          <AccordionDetails sx={{ padding: 0 }}>
            <Box
              sx={{
                zIndex: 100,
                minHeight: isMobile ? "365px" : "450px", // Increased desktop min height
                backgroundColor: "white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between", // Distribute space evenly
                padding: "16px",
              }}
            >
              {/* Enhanced Title Section with Better Info */}
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setInfoOpen(true);
                }}
                sx={{
                  cursor: "pointer",
                  padding: "12px",
                  borderRadius: "15px",
                  border: "2px solid #4a707d",
                  background: "linear-gradient(135deg, #4a707d, #6a9aaa)",
                  color: "white",
                  boxShadow: "0 4px 12px rgba(74, 112, 125, 0.3)",
                  marginBottom: "16px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(74, 112, 125, 0.4)",
                    background: "linear-gradient(135deg, #5a8090, #7aaabc)",
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: {
                      xs: "16px",
                      sm: "20px",
                      md: "24px",
                      lg: "28px",
                    },
                    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                    marginBottom: "8px",
                  }}
                >
                  {name}
                </Typography>
                
               
              </Box>

              {/* Enhanced Outcome Boxes */}
              {level_type_id > 0 && (
                <Box
                  sx={{
                    marginTop: isMobile ?"-20px" : "-40px",
                    display: "flex",
                    flexDirection: isMobile ? "row" : "row",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    gap: isMobile ? 2 : 4,
                    padding: isMobile ? 2 : 4,
                    textAlign: "center",
                  }}
                >
                  <OutcomeBox
                    type="lose"
                    coins={lose_coins}
                    borderColor="#ba55d3"
                    textColor="red"
                    dividerColor="red"
                  />
                  
                  {!isMobile && <Divider orientation="vertical" flexItem />}
                  
                  <OutcomeBox
                    type="win"
                    coins={win_coins}
                    borderColor="#4a707d"
                    textColor="green"
                    dividerColor="green"
                  />
                  
                  {!isMobile && <Divider orientation="vertical" flexItem />}
                  
                  <OutcomeBox
                    type="draw"
                    coins={draw_coins}
                    borderColor="#4169e1"
                    textColor="blue"
                    dividerColor="blue"
                  />
                </Box>
              )}

              {/* Enhanced Vote Button */}
              <Box
                sx={{
                  marginTop: "20px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "15px",
                }}
              >
                <ChessButton
                  onPress={handleSelectLevelClick}
                  shadowColor="black"
                  fontSize={isMobile === true ? mobileFontSize : regularFontSize}
                  text={t("Vote")}
                  height={isMobile === true ? "50px" : "60px"}
                  width={isMobile === true ? "170px" : "220px"}
                  icon={<HowToVoteIcon sx={{ fontSize: 20 }} />}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Main Card Title */}
      {level_type_id === "0" && (
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: isMobile ? "20px" : "25px",
            textAlign: "center",
            color: "white",
            border: "0px solid #ccc",
            boxShadow: "0 25px 25px black",
            padding: 3,
          }}
        >
          {t("Level_Select")}
        </Typography>
      )}

      {/* Enhanced Info Button */}
      {level_type_id !== "0" && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setInfoOpen(true);
          }}
          sx={{
            position: "absolute",
            top: isMobile ? 215 : 416,
            right: isMobile ? "31px" : "89px",
            color: "white",
            backgroundColor: "rgba(74, 112, 125, 0.9)",
            backdropFilter: "blur(10px)",
            border: "2px solid rgba(255,255,255,0.2)",
            width: "44px",
            height: "44px",
            zIndex: 10,
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(74, 112, 125, 1)",
              transform: "scale(1.1)",
              boxShadow: "0 4px 12px rgba(74, 112, 125, 0.4)",
            },
          }}
        >
          <InfoIcon sx={{ fontSize: isMobile ? 20 : 25, color: "white" }} />
        </IconButton>
      )}

      {/* Logo */}
      {level_type_id === "0" && (
        <Box
          sx={{
            position: "absolute",
            top: isMobile ? "330px" : "500px",
            left: "50%",
            transform: "translateX(-50%)",
            width: isMobile ? "270px" : "320px",
            height: isMobile ? "270px" : "320px",
            backgroundImage:
              'url("https://res.cloudinary.com/dnlgtek2a/image/upload/v1743426566/Pictures/logo.jpg")',
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            opacity: 1,
            zIndex: 10,
            borderRadius: "10px",
            border: "4px solid #ccc",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateX(-50%) scale(1.05)",
            }
          }}
        />
      )}

      {/* Enhanced Dialog for More Info */}
      <Dialog 
        open={infoOpen} 
        onClose={() => setInfoOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(255,255,255,0.9))",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.2)",
            boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: "linear-gradient(135deg, #4a707d, #6a9aaa)",
            color: "white",
            fontWeight: 700,
            fontSize: "1.25rem",
            borderRadius: "16px 16px 0 0",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <InfoIcon />
          {t(name)}
        </DialogTitle>
        <DialogContent sx={{ padding: "24px", fontSize: "1rem" }}>
          <Typography sx={{ 
            lineHeight: 1.6, 
            color: "#333",
            fontSize: "1rem",
          }}>
            {description}
          </Typography>
          
         
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setInfoOpen(false);
            }}
            sx={{
              background: "linear-gradient(135deg, #4a707d, #6a9aaa)",
              color: "white",
              fontWeight: 600,
              padding: "8px 24px",
              borderRadius: "12px",
              "&:hover": {
                background: "linear-gradient(135deg, #5a8090, #7aaabc)",
              }
            }}
          >
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default GameLevelSelectionItem;